// DocusealEmbed — mounts DocuSeal's <docuseal-form> web component for a signer.
//
// Loads https://cdn.docuseal.com/js/form.js once (it defines + hydrates the
// custom element), renders <docuseal-form> with the UX flags we want (no
// auto-scroll-to-field, fields in page order, no jump-to-last-field) and the
// chrome flags that strip DocuSeal's own header (WEL-28): `data-with-title`
// false drops the raw "<pdf-filename>.pdf" document title, and `data-logo`
// blanks DocuSeal's tiny out-of-place lockup (our own BrandLockup sits above
// the embed). It surfaces DocuSeal's native `completed` DOM event up to the
// page. Per product decision there is NO fallback to DocuSeal's hosted page —
// if the script fails to load we show a clean "refresh" message instead of a
// blank surface.

import { useEffect, useRef, useState } from 'react';

const FORM_JS_SRC = 'https://cdn.docuseal.com/js/form.js';
const FORM_JS_ID = 'docuseal-form-js';

export function DocusealEmbed({
  src,
  onCompleted,
}: {
  src: string;
  onCompleted: () => void;
}): React.JSX.Element {
  const ref = useRef<HTMLElement | null>(null);
  const [scriptError, setScriptError] = useState(false);

  // Load form.js exactly once across the whole SPA session.
  useEffect(() => {
    const existing = document.getElementById(FORM_JS_ID) as HTMLScriptElement | null;
    if (existing) {
      if (existing.dataset.failed === '1') setScriptError(true);
      return;
    }
    const s = document.createElement('script');
    s.id = FORM_JS_ID;
    s.async = true;
    s.src = FORM_JS_SRC;
    s.onerror = () => {
      s.dataset.failed = '1';
      setScriptError(true);
    };
    document.head.appendChild(s);
  }, []);

  // The `completed` event fires on the custom element itself — custom-element
  // events don't surface as React synthetic props, so attach a native listener.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const handler = (): void => onCompleted();
    el.addEventListener('completed', handler);
    return () => el.removeEventListener('completed', handler);
  }, [onCompleted]);

  if (scriptError) {
    return (
      <div className="alert" data-tone="warning">
        <div className="alert-body">
          <div className="alert-title">We couldn&apos;t load the signing form</div>
          Please refresh this page. If it still doesn&apos;t load, reply to our email and a real
          person will help you sign.
        </div>
      </div>
    );
  }

  return (
    <div>
      <docuseal-form
        ref={ref}
        data-src={src}
        data-autoscroll-fields="false"
        data-order-as-on-page="true"
        data-go-to-last="false"
        data-with-title="false"
        data-logo=""
      />
      <p
        style={{
          marginTop: 'var(--space-3)',
          fontSize: 'var(--text-sm)',
          color: 'var(--fg-muted)',
          textAlign: 'center',
        }}
      >
        Trouble loading the form? Refresh this page, or reply to our email and we&apos;ll help.
      </p>
    </div>
  );
}
